import React, { useState, useEffect } from "react";
import { useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard,
  Map,
  Users,
  Star,
  Calendar,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  BookOpen,
  Image,
  Sparkles,
  FileText,
  UserPlus,
  Layers,
  Database,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const userData = localStorage.getItem("admin_user");
    
    if (!token || !userData) {
      navigate("/admin/login");
      return;
    }

    setUser(JSON.parse(userData));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  const menuItems = [
    { path: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/hero-slides", icon: Layers, label: "Carrousel Hero" },
    { path: "/admin/circuits", icon: Map, label: "Circuits" },
    { path: "/admin/reservations", icon: BookOpen, label: "Réservations" },
    { path: "/admin/trip-dates", icon: Calendar, label: "Dates de voyage" },
    { path: "/admin/stages", icon: Sparkles, label: "Stages & Retraites" },
    { path: "/admin/gallery", icon: Image, label: "Galerie" },
    { path: "/admin/blog", icon: FileText, label: "Blog" },
    { path: "/admin/experiences", icon: MessageCircle, label: "Retour d'expériences" },
    { path: "/admin/testimonials", icon: Star, label: "Témoignages" },
    { path: "/admin/team", icon: Users, label: "Équipe" },
    { path: "/admin/contacts", icon: MessageSquare, label: "Messages" },
    { path: "/admin/collaborators", icon: UserPlus, label: "Collaborateurs" },
    { path: "/admin/users", icon: Settings, label: "Utilisateurs" },
    { path: "/admin/seed-data", icon: Database, label: "Import Données" },
  ];

  const isActive = (path) => location.pathname === path;

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full ${mobile ? "" : "w-64 border-r border-border"}`}>
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <span className="text-white font-serif text-xl">X</span>
          </div>
          <div>
            <span className="font-serif text-lg font-semibold block">XploreSahara</span>
            <span className="text-xs text-muted-foreground">Administration</span>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => mobile && setSidebarOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              isActive(item.path)
                ? "bg-primary text-white"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
            data-testid={`admin-nav-${item.path.split("/").pop()}`}
          >
            <item.icon className="h-5 w-5" />
            <span>{item.label}</span>
            {isActive(item.path) && <ChevronRight className="h-4 w-4 ml-auto" />}
          </Link>
        ))}
      </nav>

      {/* User & Logout */}
      <div className="p-4 border-t border-border">
        {user && (
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-semibold">{user.name?.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
          onClick={handleLogout}
          data-testid="admin-logout-btn"
        >
          <LogOut className="h-5 w-5" />
          Déconnexion
        </Button>
      </div>
    </div>
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-muted flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block bg-card">
        <Sidebar />
      </div>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <Sidebar mobile />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-card border-b border-border px-4 py-3 flex items-center gap-4 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-serif font-semibold">XploreSahara Admin</span>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
